/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import GraphqlService from '../../service/graphqlService';
import PageBanner from '../../components/utils/PageBanner';
import {
  PlusIcon,
  TrashIcon,
  UserMinusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  UserPlusIcon,
  UserGroupIcon,
  KeyIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  SparklesIcon,
  LockClosedIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { useSnackbar } from '../../providers/SnackbarContext'; // Added import for useSnackbar
import { isProjectRoleCombinationAssigned, canCallerManageGroup } from '../../utils/userManagementHelpers';
import useRole from '../../hooks/useGetRole';
import RoleBadge from '../../components/admin/RoleBadge';
import RolesLegend from '../../components/admin/RolesLegend';
import AuditActivityPanel from '../../components/admin/AuditActivityPanel';
import { getRoleMetadata } from '../../config/roleMetadata';
import { CHINA_ACCESS_GROUP } from '../../config/regions';
import { GlobeAltIcon } from '@heroicons/react/24/outline';

const ROLES = [
  "telemetry",
  "developer",
  "viewer",
  "releaseManager",
  "superadmin",
  "userManagement",
  "l3Support",
  "rootAccess",
  "certificateadmin"
];

export default function UserManagement() {
  const [deletingUserId, setDeletingUserId] = useState(null); // Add state to track which user is being deleted

  const [isSSOInfoBannerOpen, setIsSSOInfoBannerOpen] = useState(false); // State for SSO info banner
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [manualPassword, setManualPassword] = useState('');
  const [isPasswordAuto, setIsPasswordAuto] = useState(true);

  const [searchText, setSearchText] = useState('');
  const [selectedFilterProject, setSelectedFilterProject] = useState('');
  const [selectedFilterRole, setSelectedFilterRole] = useState('');
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  // Filters actually applied to the query. Decoupled from the input state so a
  // search/clear runs against committed values (the query's `queryFn` closes
  // over these, never over the raw input state).
  const [appliedFilters, setAppliedFilters] = useState({ searchText: '', project: '', role: '' });
  const [users, setUsers] = useState([]);

  // State for "Add/Manage Groups" Modal
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [userForModalAction, setUserForModalAction] = useState(null); // Stores the user object for modal actions
  const [modalRole, setModalRole] = useState(''); // Persistent role selection
  const [modalProject, setModalProject] = useState('');
  const [modalProjectSearchTerm, setModalProjectSearchTerm] = useState('');
  const [isModalProjectDropdownOpen, setIsModalProjectDropdownOpen] = useState(false); // For modal

  // State for Remove Group Confirmation Modal
  const [isRemoveGroupConfirmModalOpen, setIsRemoveGroupConfirmModalOpen] = useState(false);
  const [groupToRemove, setGroupToRemove] = useState(null); // { userId, groupName }

  const openSnackBar = useSnackbar(); // Added hook call

  const queryClient = useQueryClient(); // For invalidating dependent queries (e.g. audit feed)

  // Current user's scope. Backend enforces all authorization authoritatively;
  // these values only drive UX (which projects/roles are offered in pickers,
  // and which existing groups can be removed).
  const { isRoot, isGlobalUserManager, ownedProjects } = useRole();

  // Roles offered in the picker. Non-root users cannot assign `rootAccess` (FR-014).
  const availableRoles = isRoot ? ROLES : ROLES.filter((role) => role !== 'rootAccess');

  // Temporary password for a pre-created (non-SSO) account. Uses the CSPRNG, not Math.random.
  const generateStrongPassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{};:,.<>/?';
    const allChars = lowercase + uppercase + numbers + symbols;
    const length = 16;

    const pick = (charset, count) => {
      const bytes = new Uint32Array(count);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => charset[byte % charset.length]);
    };

    const chars = [
      ...pick(lowercase, 1),
      ...pick(uppercase, 1),
      ...pick(numbers, 1),
      ...pick(symbols, 1),
      ...pick(allChars, length - 4),
    ];

    // Fisher-Yates shuffle so the guaranteed characters are not always in the first positions.
    const order = new Uint32Array(chars.length);
    window.crypto.getRandomValues(order);
    for (let i = chars.length - 1; i > 0; i--) {
      const j = order[i] % (i + 1);
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
  };

  const fetchProjects = useCallback(async () => GraphqlService.getProjects(), []);


  // Fetch Projects Data
  const {
    data: projectsData,
  } = useQuery(['projectsLayout'], fetchProjects, {
    refetchOnWindowFocus: false, // Disable refetching on window focus
    refetchOnReconnect: false, // Disable refetching on reconnect
    refetchInterval: false, // Disable polling
  });


  // Search/filter users by committed (applied) filters.
  const {
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    error: usersError,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ['cognitoUsersFiltered', appliedFilters],
    queryFn: async () => GraphqlService.getCognitoUsersFiltered({
      searchText: appliedFilters.searchText,
      project: appliedFilters.project,
      role: appliedFilters.role,
    }),
    onSuccess: (res) => {
      setUsers(res?.result || []);
    },
    onError: (error) => {
      openSnackBar(error.message || 'Error fetching users', 'error');
      setUsers([]);
    },
  });

  // Automatic search. Debounce committing the raw inputs to `appliedFilters` so
  // typing doesn't fire a request on every keystroke — it only commits ~400ms
  // after the user pauses. Project/role selects flow through the same effect, so
  // changing a select also commits (effectively instant given the short delay).
  // The committed `appliedFilters` then drives the fetch effect above.
  React.useEffect(() => {
    const handler = setTimeout(() => {
      const trimmedSearch = searchText.trim();
      setHasActiveFilters(Boolean(trimmedSearch || selectedFilterProject || selectedFilterRole));
      setAppliedFilters({
        searchText: trimmedSearch,
        project: selectedFilterProject,
        role: selectedFilterRole,
      });
    }, 200);
    return () => clearTimeout(handler);
  }, [searchText, selectedFilterProject, selectedFilterRole]);



  // Create User. Pre-creating a non-SSO account is how access is granted before the
  // person's first SSO login (see product-knowledge/PK-005).
  const { mutate: createUser, isLoading: isCreatingUser } = useMutation({
    mutationFn: async ({ email: newEmail, password }) => GraphqlService.createCognitoUser(newEmail, password),
    onSuccess: () => {
      refetchUsersAfterModification();
      resetCreateUserForm();
      setIsCreateUserModalOpen(false);
      openSnackBar('User created successfully!', 'success');
    },
    onError: (error) => {
      openSnackBar(`Error creating user: ${error.message}`, 'error');
    }
  });

  // Delete User
  const { mutate: deleteUser } = useMutation({
    mutationFn: async (userId) => GraphqlService.deleteCognitoUserByUserId(userId),
    onSuccess: () => {
      // Filter out the deleted user from the current users list
      setUsers(currentUsers => currentUsers.filter(user => user.username !== deletingUserId));
      setDeletingUserId(null); // Reset deletingUserId on success
      refetchUsersAfterModification();
      openSnackBar('User deleted successfully!', 'success');
    },
    onError: (error) => {
      setDeletingUserId(null); // Reset deletingUserId on error too
      openSnackBar(`Error deleting user: ${error.message}`, 'error');
    }
  });

  const handleDeleteUser = (userId) => {
    // Removed window.confirm. Deletion will proceed directly.
    // The snackbar notifications are handled by the deleteUser mutation's onSuccess/onError.
    setDeletingUserId(userId);
    deleteUser(userId);
  };

  // Add Group to User
  const { mutate: addGroup, isLoading: isAddingGroup } = useMutation({
    mutationFn: async ({ userId, projectPermission, role, rawGroup }) => {
      const group = rawGroup || (role === 'rootAccess' ? 'rootAccess' : `${projectPermission}:${role}`);
      let groupWasJustCreated = false;

      // Step 1: Check if group exists.
      const groupExistsResponse = await GraphqlService.checkGroupExists(group);

      // Ensure groupExistsResponse and groupExistsResponse.exists are valid

      if (!groupExistsResponse) {
        // Group does not exist, attempt to create it directly.
        try {
          await GraphqlService.createCognitoGroup(group);
          groupWasJustCreated = true;
          // Snackbar for group creation will be handled in onSuccess to combine messages
        } catch (creationError) {
          // If group creation fails, throw an error to be caught by onError
          throw new Error(`Failed to create group '${group}': ${creationError.message}.`);
        }
      }


      // Step 2: Add user to the group.
      // Assumes GraphqlService.addGroupToUser throws on failure.
      await GraphqlService.addGroupToUser(userId, group);

      return { groupName: group, groupWasJustCreated }; // Return info for onSuccess
    },
    onSuccess: (data) => { // data will contain { groupName, groupWasJustCreated }
      refetchUsersAfterModification();
      // Keep modalRole selected for next assignment (persistent role selection)
      setModalProject(''); // Reset only the project
      setModalProjectSearchTerm('');
      setIsModalProjectDropdownOpen(false); // Reset dropdown state
      // refresh the content of the modal without closing it
      setUserForModalAction(prevUser => {
        if (!prevUser) return null; // If no user is set, return null
        const updatedGroups = prevUser.groups ? `${prevUser.groups},${data.groupName}` : data.groupName;
        return { ...prevUser, groups: updatedGroups };
      });
      if (data.groupWasJustCreated) {
        openSnackBar(`Group '${data.groupName}' created and user added successfully!`, 'success');
      } else {
        openSnackBar(`User added to group '${data.groupName}' successfully!`, 'success');
      }
    },
    onError: (error) => {
      // This will catch errors from checkGroupExists, createCognitoGroup, or addGroupToUser
      openSnackBar(`Operation failed: ${error.message}`, 'error');
    }
  });

  // Remove Group from User
  const { mutate: removeGroup, isLoading: isRemovingGroup } = useMutation({
    mutationFn: async ({ userId, groupName }) => GraphqlService.removeGroupFromUser(userId, groupName.trim()),
    onSuccess: (data, variables) => { // Added variables to access groupName and userId
      refetchUsersAfterModification();
      openSnackBar('Group removed successfully!', 'success');

      // Update userForModalAction to reflect the removed group in the modal
      if (userForModalAction && userForModalAction.username === variables.userId) {
        setUserForModalAction(prevUser => {
          if (!prevUser || !prevUser.groups) return prevUser;
          const updatedGroups = prevUser.groups
            .split(',')
            .filter(g => g !== variables.groupName)
            .join(',');
          return { ...prevUser, groups: updatedGroups };
        });
      }
    },
    onError: (error) => {
      openSnackBar(`Error removing group: ${error.message}`, 'error');
    }
  });

  const refetchUsersAfterModification = () => {
    refetchUsers();
    // Refresh the Recent Activity feed so the new audit entry shows up immediately.
    queryClient.invalidateQueries(['userManagementAudit']);
  };

  const handleOpenSSOInfoBanner = () => {
    setIsSSOInfoBannerOpen(true);
  };

  const resetCreateUserForm = () => {
    setEmail('');
    setManualPassword('');
    setIsPasswordAuto(true);
  };

  const handleOpenCreateUserModal = () => {
    resetCreateUserForm();
    setIsCreateUserModalOpen(true);
  };

  const handleCreateUser = (e) => {
    e.preventDefault();
    if (!email || (!isPasswordAuto && !manualPassword)) {
      openSnackBar('Email is required, and the temporary password cannot be empty when set manually.', 'error');
      return;
    }
    createUser({ email, password: isPasswordAuto ? generateStrongPassword() : manualPassword });
  };

  // Called when "Add Group" button in the user row is clicked
  const handleOpenAddGroupModal = (user) => {
    setUserForModalAction(user);
    // Keep modalRole unchanged (persistent across users)
    // Reset only the project selection and search term
    setModalProject(''); // Reset project for new user
    setModalProjectSearchTerm('');
    setIsModalProjectDropdownOpen(false);
    setIsAddGroupModalOpen(true);
  };

  // Called when the form inside the modal is submitted
  const handleModalFormSubmit = (e) => {
    e.preventDefault();
    if (!userForModalAction || !modalRole) {
      openSnackBar('User and Role are required.', 'error');
      return;
    }
    if (modalRole !== 'rootAccess' && !modalProject) {
      openSnackBar('Project is required for this role.', 'error');
      return;
    }
    addGroup({
      userId: userForModalAction.username,
      projectPermission: modalRole === 'rootAccess' ? '' : modalProject,
      role: modalRole
    });
  };

  const handleRemoveGroupFromUser = (userId, groupName) => {
    setGroupToRemove({ userId, groupName });
    setIsRemoveGroupConfirmModalOpen(true);
  };

  // Grants the China data region access group. Root-only in the UI (mirrors
  // canCallerManageGroup, which restricts non `project:role` groups like
  // `china-access` to rootAccess callers).
  const handleGrantChinaAccess = (user) => {
    addGroup({ userId: user.username, rawGroup: CHINA_ACCESS_GROUP });
  };

  const confirmRemoveGroup = () => {
    if (groupToRemove) {
      removeGroup({ userId: groupToRemove.userId, groupName: groupToRemove.groupName });
      setIsRemoveGroupConfirmModalOpen(false);
      setGroupToRemove(null);
    }
  };

  const commonInputStyle = "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
  const commonButtonStyle = "inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-semibold rounded-lg shadow-md text-white transition-all hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:translate-y-0 disabled:shadow-md";
  const primaryButtonStyle = `${commonButtonStyle} bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 focus:ring-indigo-500`;
  const successButtonStyle = `${commonButtonStyle} bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 focus:ring-emerald-500`;
  const dangerButtonStyle = `${commonButtonStyle} bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 focus:ring-red-500`;
  const clearButtonStyle = `${commonButtonStyle} bg-gradient-to-r from-slate-500 to-gray-600 hover:from-slate-600 hover:to-gray-700 focus:ring-gray-500`;
  const iconStyle = "h-5 w-5 mr-2";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageBanner title="USER MANAGEMENT" />
      <div className="mx-auto max-w-7xl p-6 space-y-6">

        {/* Intro / help header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-sky-500 px-6 py-7 text-white shadow-xl ring-1 ring-white/10">
          <div className="absolute -right-10 -top-10 opacity-20">
            <UsersIcon className="h-48 w-48" />
          </div>
          <div className="absolute -left-12 bottom-0 h-40 w-40 rounded-full bg-white/10 blur-2xl" aria-hidden="true" />
          <div className="relative flex items-start gap-4">
            <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/15 shadow-inner backdrop-blur">
              <UserGroupIcon className="h-8 w-8" />
            </span>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Manage people &amp; access</h2>
              <p className="mt-1.5 max-w-3xl text-sm text-indigo-50">
                Find users by email, create new accounts, and grant project-scoped roles.
                Each permission is a <span className="font-semibold">role granted on a single project</span>{' '}
                (shown as <code className="rounded bg-white/20 px-1 py-0.5 text-xs">project:role</code>).
                You can review or remove access at any time — changes take effect immediately.
              </p>
            </div>
          </div>

          {/* Quick tips strip */}
          <div className="relative mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 backdrop-blur transition-colors hover:bg-white/15">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Search</p>
                <p className="truncate text-xs text-indigo-50/80">Look up users by email</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 backdrop-blur transition-colors hover:bg-white/15">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                <UserPlusIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Create</p>
                <p className="truncate text-xs text-indigo-50/80">Add new portal accounts</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 backdrop-blur transition-colors hover:bg-white/15">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                <ShieldCheckIcon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Assign roles</p>
                <p className="truncate text-xs text-indigo-50/80">Grant project-scoped access</p>
              </div>
            </div>
          </div>
        </div>

        {/* Roles explained legend */}
        <RolesLegend />

        {/* Search + create card */}
        <div className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-lg ring-1 ring-gray-100/50 backdrop-blur">
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-md">
              <MagnifyingGlassIcon className="h-5 w-5 text-white" />
            </span>
            <div>
              <h3 className="text-base font-bold text-gray-900">Find &amp; create users</h3>
              <p className="text-xs text-gray-500">Search an existing account or onboard someone new.</p>
            </div>
          </div>
          {/* Search runs automatically (debounced text + instant selects); the
              form submit is a no-op so pressing Enter doesn't reload the page. */}
          <form onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-3">
            <label htmlFor="searchText" className="block text-sm font-semibold text-gray-700">
              Search users
            </label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex-grow">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="relative md:col-span-2">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <MagnifyingGlassIcon className="h-5 w-5 text-indigo-400" />
                    </span>
                    <input
                      id="searchText"
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Name or email (e.g. cabello)"
                      className={`pl-10 ${commonInputStyle}`}
                    />
                  </div>
                  <select
                    aria-label="Filter by project"
                    value={selectedFilterProject}
                    onChange={(e) => setSelectedFilterProject(e.target.value)}
                    className={commonInputStyle}
                  >
                    <option value="">All projects</option>
                    {(projectsData || []).map((proj) => (
                      <option key={proj.permission} value={proj.permission}>
                        {proj.name || proj.permission}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Filter by role"
                    value={selectedFilterRole}
                    onChange={(e) => setSelectedFilterRole(e.target.value)}
                    className={commonInputStyle}
                  >
                    <option value="">All roles</option>
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>{getRoleMetadata(role).label}</option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Search by partial text and refine by project and role.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 sm:self-start">
                <button
                  type="button"
                  onClick={() => {
                    setSearchText('');
                    setSelectedFilterProject('');
                    setSelectedFilterRole('');
                    setHasActiveFilters(false);
                    // Commit empty filters; the effect refetches with cleared values.
                    setAppliedFilters({ searchText: '', project: '', role: '' });
                  }}
                  className={`${clearButtonStyle} whitespace-nowrap`} // Added whitespace-nowrap
                >
                  Clear Search
                </button>
                <button
                  type="button"
                  onClick={handleOpenCreateUserModal}
                  className={`${successButtonStyle} whitespace-nowrap`}
                >
                  <PlusIcon className={iconStyle} />
                  Create New User
                </button>
                <button
                  type="button"
                  onClick={handleOpenSSOInfoBanner}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  <InformationCircleIcon className={iconStyle} />
                  How access works
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Users results */}
        <div className="space-y-4">
          {usersError && (
            <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
              <span>Error loading users: {usersError.message}</span>
            </div>
          )}

          {!isLoadingUsers && users?.length === 0 && hasActiveFilters && !usersError && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                <MagnifyingGlassIcon className="h-7 w-7 text-gray-400" />
              </span>
              <p className="mt-3 text-sm font-medium text-gray-700">No users found with the selected filters.</p>
              <p className="mt-1 text-xs text-gray-400">Try another text, project, or role.</p>
            </div>
          )}
          {!isLoadingUsers && users?.length === 0 && !hasActiveFilters && !usersError && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
                <UsersIcon className="h-7 w-7 text-indigo-400" />
              </span>
              <p className="mt-3 text-sm font-medium text-gray-700">Use the filters to find users</p>
              <p className="mt-1 max-w-sm text-xs text-gray-400">
                You can search by partial text and filter by project/role.
              </p>
            </div>
          )}

          {users && users?.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg ring-1 ring-gray-100/50">
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
                <table className="min-w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gradient-to-r from-indigo-50 via-violet-50 to-sky-50">
                    <tr>
                      <th scope="col" className="px-4 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Email</th>
                      <th scope="col" className="px-4 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Groups</th>
                      <th scope="col" className="px-4 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Last Login</th>
                      <th scope="col" className="px-4 py-4 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {users.map((user) => (
                      <tr
                        key={user.username}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleOpenAddGroupModal(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOpenAddGroupModal(user);
                          }
                        }}
                        className="cursor-pointer transition-colors hover:bg-indigo-50/40 focus:outline-none focus:bg-indigo-50/60"
                      >
                        <td className="px-4 py-4">
                          <span className="text-sm font-semibold text-gray-900 break-all">{user.email}</span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {user.groups && typeof user.groups === 'string' && user?.groups?.length > 0 ? (() => {
                            const roleCount = user.groups.split(',').filter(Boolean).length;
                            return (
                              <div className="flex flex-wrap items-center gap-1.5">
                                {user.groups.split(',').map((group) => (
                                  <RoleBadge key={group} group={group} size="sm" />
                                ))}
                                <span
                                  title="Total roles assigned to this user"
                                  className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold text-indigo-600 ring-1 ring-indigo-100"
                                >
                                  {roleCount} {roleCount === 1 ? 'role' : 'roles'}
                                </span>
                              </div>
                            );
                          })() : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                              No access yet
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleString() : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-400 ring-1 ring-gray-100">Never logged in</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-medium">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAddGroupModal(user);
                              }}
                              className="inline-flex items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 p-2 text-indigo-700 transition-colors hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                              title="Manage Permissions — grant or remove project roles for this user"
                              aria-label="Manage Permissions"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteUser(user.username);
                              }}
                              disabled={deletingUserId === user.username}
                              className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-2 text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50"
                              title="Delete User — permanently delete this user"
                              aria-label="Delete User"
                            >
                              {deletingUserId === user.username ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-200 border-t-red-600" />
                              ) : (
                                <TrashIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        { /* Loading state */}
        <div className="mt-2">
          {(isLoadingUsers || isFetchingUsers) && (
            <div className="flex items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-6 text-sm text-gray-500 shadow-sm">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
              Loading users…
            </div>
          )}
        </div>

        {/* Recent Activity audit feed */}
        <AuditActivityPanel />
      </div>

      {/* Create User Modal */}
      {isCreateUserModalOpen && (
        <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                  <UserPlusIcon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold">Create New User</h3>
                  <p className="text-xs text-emerald-50">Add a new person to the IoT Portal</p>
                </div>
              </div>
              <button
                onClick={() => setIsCreateUserModalOpen(false)}
                className="text-white/80 transition-colors hover:text-white focus:outline-none"
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-5 px-6 py-6">
              <div className="flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Create the account for this email and then use
                  <span className="font-semibold"> Manage Permissions</span> to grant project roles.
                  When the person signs in with SSO, those roles are carried over automatically.
                </span>
              </div>
              <div>
                <label htmlFor="email-modal" className="mb-1 block text-sm font-semibold text-gray-700">Email address</label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </span>
                  <input id="email-modal" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={`pl-10 ${commonInputStyle}`} placeholder="user@example.com" />
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <label htmlFor="autoPasswordCheckbox" className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    id="autoPasswordCheckbox"
                    checked={isPasswordAuto}
                    onChange={() => setIsPasswordAuto(!isPasswordAuto)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                    <SparklesIcon className="h-4 w-4 text-indigo-500" />
                    Generate Password Automatically
                  </span>
                </label>
                <p className="mt-1.5 pl-7 text-xs text-gray-500">
                  Recommended — a strong temporary password is created. SSO users never need it.
                </p>
              </div>

              {!isPasswordAuto && (
                <div>
                  <label htmlFor="manualPassword-modal" className="mb-1 block text-sm font-semibold text-gray-700">Temporary password</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </span>
                    <input
                      id="manualPassword-modal"
                      type="password"
                      value={manualPassword}
                      onChange={(e) => setManualPassword(e.target.value)}
                      required={!isPasswordAuto}
                      className={`pl-10 ${commonInputStyle}`}
                      placeholder="Enter manual password"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    Share this password securely with the user. They must change it at first login.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsCreateUserModalOpen(false)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button type="submit" disabled={isCreatingUser} className={`${successButtonStyle} disabled:opacity-60`}>
                  <CheckCircleIcon className={iconStyle} />
                  {isCreatingUser ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SSO Info Banner Modal */}
      {isSSOInfoBannerOpen && (
        <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between bg-gradient-to-r from-sky-600 to-blue-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                  <UserPlusIcon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold">User Registration via SSO</h3>
                  <p className="text-xs text-sky-50">How users are added to the portal</p>
                </div>
              </div>
              <button
                onClick={() => setIsSSOInfoBannerOpen(false)}
                className="text-white/80 transition-colors hover:text-white focus:outline-none"
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-5 px-6 py-6">
              <div className="flex items-start gap-3 rounded-lg bg-sky-50 px-4 py-3 text-sm text-sky-800">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>
                  Grant access first, then have the person sign in — not the other way around.
                </span>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">User Registration Flow:</h4>
                  <ol className="space-y-3 text-sm text-gray-700">
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">1</span>
                      <span>A user with the <code className="rounded bg-sky-100 px-1 py-0.5 text-xs font-semibold">userManagement</code> role creates the account with <span className="font-semibold">Create New User</span> (email only)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">2</span>
                      <span>Project roles are assigned to that account with <span className="font-semibold">Manage Permissions</span></span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">3</span>
                      <span>The person signs in with their SSO credentials for the first time</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700">4</span>
                      <span>Their SSO account inherits the assigned roles automatically and the placeholder account is removed</span>
                    </li>
                  </ol>
                </div>

                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm text-amber-900">
                    <span className="font-semibold">Already signed in once?</span> Then the account already exists — just search for it by email and grant the permissions.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setIsSSOInfoBannerOpen(false)}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Manage Groups Modal */}
      {isAddGroupModalOpen && userForModalAction && (
        <div className="fixed inset-0 z-50 flex h-full w-full items-start justify-center overflow-y-auto bg-gray-900/60 p-4 py-6 backdrop-blur-sm sm:items-center">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                  <ShieldCheckIcon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold">Manage Permissions</h3>
                  <p className="text-xs text-indigo-50">{userForModalAction.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsAddGroupModalOpen(false);
                  setModalProjectSearchTerm('');
                  setIsModalProjectDropdownOpen(false);
                }}
                className="text-white/80 transition-colors hover:text-white focus:outline-none"
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto px-6 pt-6 pb-8">
              <div className="flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-700">
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  A permission grants one <span className="font-semibold">role</span> on one
                  <span className="font-semibold"> project</span> (stored as
                  <code className="mx-1 rounded bg-indigo-100 px-1 py-0.5">project:role</code>).
                  Pick a role to see what it can do, then choose the project.
                </span>
              </div>

              {/* Display Current Groups with Remove Option */}
              <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4">
                <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-violet-800">
                  <KeyIcon className="h-4 w-4 text-violet-400" />
                  Current Groups:
                </h4>
                {userForModalAction.groups && userForModalAction.groups?.length > 0 ? (
                  <ul className="max-h-72 space-y-2 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50 p-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {userForModalAction.groups.split(',').map((group) => {
                      const canManage = canCallerManageGroup(group, { isRoot, isGlobalUserManager, ownedProjects });
                      return (
                        <li key={group} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow-sm">
                          <RoleBadge group={group} size="sm" />
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => handleRemoveGroupFromUser(userForModalAction.username, group)}
                              disabled={isRemovingGroup}
                              className="rounded-md p-1.5 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-500"
                              title="Remove this permission"
                            >
                              <UserMinusIcon className="h-5 w-5" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="rounded-lg bg-gray-50 px-3 py-3 text-sm text-gray-500">
                    No permissions assigned yet. Grant access using the form below.
                  </p>
                )}
              </div>

              {/* China Data Region Access — root-only, since `china-access` is not a
                  `project:role` group and cannot be granted via the role/project
                  form below (see canCallerManageGroup). */}
              {isRoot && (
                <div className="flex items-center justify-between rounded-xl border border-sky-100 bg-sky-50/50 p-4">
                  <div className="flex items-center gap-2">
                    <GlobeAltIcon className="h-5 w-5 text-sky-500" />
                    <div>
                      <p className="text-sm font-semibold text-sky-800">China Data Region Access</p>
                      <p className="text-xs text-sky-700">
                        {userForModalAction.groups?.split(',').includes(CHINA_ACCESS_GROUP)
                          ? 'This user can access the China data region.'
                          : 'Grants the `china-access` group so this user can switch to the China data region.'}
                      </p>
                    </div>
                  </div>
                  {!userForModalAction.groups?.split(',').includes(CHINA_ACCESS_GROUP) && (
                    <button
                      type="button"
                      onClick={() => handleGrantChinaAccess(userForModalAction)}
                      disabled={isAddingGroup}
                      className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      Grant
                    </button>
                  )}
                </div>
              )}

              <hr className="border-gray-100" />

              {/* Add New Group Form */}
              <form onSubmit={handleModalFormSubmit} className="space-y-4 pb-6">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                    Grant a new permission
                  </span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="modalRole" className="block text-sm font-medium text-gray-700">Role</label>
                    {modalRole && (
                      <button
                        type="button"
                        onClick={() => setModalRole('')}
                        className="text-xs text-gray-500 underline hover:text-gray-700"
                        title="Clear role selection"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>
                  <select
                    id="modalRole"
                    value={modalRole}
                    onChange={(e) => {
                      setModalRole(e.target.value);
                      if (e.target.value === 'rootAccess') {
                        setModalProject('');
                        setModalProjectSearchTerm('');
                        setIsModalProjectDropdownOpen(false);
                      }
                    }}
                    required
                    disabled={userForModalAction.groups?.includes('rootAccess')}
                    className={`${commonInputStyle} ${userForModalAction.groups?.includes('rootAccess') ? 'bg-gray-200' : ''}`}
                  >
                    <option value="">Select Role</option>
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>{getRoleMetadata(role).label}</option>
                    ))}
                  </select>
                  {modalRole && !userForModalAction.groups?.includes('rootAccess') && (() => {
                    const meta = getRoleMetadata(modalRole);
                    const { Icon } = meta;
                    return (
                      <div className={`mt-2 flex items-start gap-2.5 rounded-lg p-3 ring-1 ${meta.color.iconBg} ${meta.color.ring}`}>
                        <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${meta.color.iconText}`} />
                        <div>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.color.badge}`}>
                            {meta.label}
                          </span>
                          <p className="mt-1 text-xs leading-relaxed text-gray-600">{meta.description}</p>
                        </div>
                      </div>
                    );
                  })()}
                  {userForModalAction.groups?.includes('rootAccess') && <p className="mt-1 text-xs text-gray-500">User already has rootAccess - all roles are accessible.</p>}
                </div>

              {/* Project Selector for Modal */}
              <div className="relative">
                <label htmlFor="modalProjectSearchInput" className="mb-1.5 block text-sm font-medium text-gray-700">Project</label>
                <input
                  id="modalProjectSearchInput"
                  type="text"
                  placeholder={modalProject ? projectsData?.find(p => p.permission === modalProject)?.name || modalProject : "Select Project or type to search..."}
                  value={modalProjectSearchTerm}
                  onChange={(e) => {
                    setModalProjectSearchTerm(e.target.value);
                    setModalProject(''); // Clear selection when typing
                    if (!isModalProjectDropdownOpen) setIsModalProjectDropdownOpen(true);
                  }}
                  onFocus={() => setIsModalProjectDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setIsModalProjectDropdownOpen(false), 150)} // Delay to allow click
                  disabled={modalRole === 'rootAccess' || userForModalAction.groups?.includes('rootAccess')}
                  className={`${commonInputStyle} ${(modalRole === 'rootAccess' || userForModalAction.groups?.includes('rootAccess')) ? 'bg-gray-200' : ''}`}
                />
                {isModalProjectDropdownOpen && modalRole !== 'rootAccess' && !userForModalAction.groups?.includes('rootAccess') && (
                  <div className="absolute bottom-full left-0 z-50 mb-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                    {(() => {
                      const filteredProjects = projectsData
                        ?.filter(proj => {
                          // Non-root users can only target projects they own (FR-014).
                          // Backend re-validates ownership; this is UX-only scoping.
                          const isOwnedProject = isRoot || ownedProjects.includes(proj.permission);
                          if (!isOwnedProject) return false;
                          // Filter by search term
                          const matchesSearch = !modalProjectSearchTerm || 
                            proj.name.toLowerCase().includes(modalProjectSearchTerm.toLowerCase()) || 
                            proj.permission.toLowerCase().includes(modalProjectSearchTerm.toLowerCase());
                          // Exclude only if the same (project + role) combination is already assigned
                          const isNotAssignedWithRole = !isProjectRoleCombinationAssigned(
                            userForModalAction.groups,
                            proj.permission,
                            modalRole
                          );
                          return matchesSearch && isNotAssignedWithRole;
                        });
                      
                      return (
                        <>
                          {filteredProjects?.map((proj) => (
                            <div
                              key={proj.permission}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onMouseDown={() => { // Use onMouseDown to fire before onBlur
                                setModalProject(proj.permission);
                                setModalProjectSearchTerm(proj.name || proj.permission);
                                setIsModalProjectDropdownOpen(false);
                              }}
                            >
                              {proj.name || proj.permission}
                            </div>
                          ))}
                          {filteredProjects?.length === 0 && (
                            <div className="px-4 py-2 text-gray-500">
                              No projects found.
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                {modalRole === 'rootAccess' && <p className="mt-1 text-xs text-gray-500">Project is not applicable for rootAccess role.</p>}
                {userForModalAction.groups?.includes('rootAccess') && <p className="mt-1 text-xs text-gray-500">User already has rootAccess - all projects are accessible.</p>}
              </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddGroupModalOpen(false);
                      setModalProjectSearchTerm('');
                      setIsModalProjectDropdownOpen(false);
                    }}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={isAddingGroup || !modalRole || (modalRole !== 'rootAccess' && !modalProject)}
                    className={`${primaryButtonStyle} disabled:opacity-50`}
                  >
                    <PlusIcon className={iconStyle} />
                    {isAddingGroup ? 'Adding...' : 'Add Group'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Remove Group Confirmation Modal */}
      {isRemoveGroupConfirmModalOpen && groupToRemove && (
        <div className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between bg-gradient-to-r from-red-600 to-rose-600 px-6 py-5 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                  <ExclamationTriangleIcon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold">Confirm Removal</h3>
                  <p className="text-xs text-red-50">This action cannot be undone</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsRemoveGroupConfirmModalOpen(false);
                  setGroupToRemove(null);
                }}
                className="text-white/80 transition-colors hover:text-white focus:outline-none"
                aria-label="Close modal"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-5 px-6 py-6">
              <p className="text-sm text-gray-700">
                You are about to revoke this permission from the user. They will immediately lose the
                access it grants.
              </p>
              <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-3">
                <RoleBadge group={groupToRemove.groupName} size="sm" />
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsRemoveGroupConfirmModalOpen(false);
                    setGroupToRemove(null);
                  }}
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmRemoveGroup}
                  disabled={isRemovingGroup}
                  className={`${dangerButtonStyle} disabled:opacity-60`}
                >
                  <TrashIcon className={iconStyle} />
                  {isRemovingGroup ? 'Removing...' : 'Remove Group'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
